import React, { useState } from "react";
import { formatUSD } from "../../shared/utils";

/* What you're working toward, and what you've decided not to do.
   Deliberately plain. The point of writing collecting goals down is that
   collecting stops feeling boundless, so a screen that answered with ten
   metrics and a dashboard would be working against itself. */

const KINDS = [
  { id: "rule", label: "A rule", hint: "Singles over slabs. No sealed." },
  { id: "set", label: "Finish a list", hint: "Progress fills itself in" },
  { id: "value", label: "Collection target", hint: "A number to reach" },
];

function Progress({ done, total, label }) {
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  return (
    <div className="goal-progress">
      <div className="goal-bar">
        <div className="goal-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="goal-progress-label">{label} · {pct}%</div>
    </div>
  );
}

export default function Goals({
  goals,
  addGoal,
  removeGoal,
  toggleDone,
  lists,
  listProgress,     // (listId) => { done, total } | null
  collectionValue,
}) {
  const [adding, setAdding] = useState(null);   // which kind is being added
  const [text, setText] = useState("");
  const [listId, setListId] = useState("");
  const [target, setTarget] = useState("");

  const reset = () => { setAdding(null); setText(""); setListId(""); setTarget(""); };

  const submit = () => {
    if (adding === "set") {
      const list = lists.find((l) => l.id === listId);
      if (!list) return;
      addGoal({ kind: "set", text: `Finish ${list.name}`, listId: list.id });
    } else if (adding === "value") {
      const n = Number(target);
      if (!(n > 0)) return;
      addGoal({ kind: "value", text: `Collection to ${formatUSD(n)}`, target: n });
    } else {
      addGoal({ kind: "rule", text });
    }
    reset();
  };

  return (
    <>
      <h2 className="section-title">Goals</h2>
      <p className="goals-intro">
        What you're working toward, and what you've decided not to do. Written down
        so it's in front of you before you buy, not after.
      </p>

      {goals.length === 0 && !adding && (
        <p className="msg-empty">
          Nothing set yet. A goal can be a rule you're keeping to — “singles over
          slabs”, “no sealed” — or one list you're finishing before starting another.
        </p>
      )}

      {goals.length > 0 && (
        <ul className="goal-list">
          {goals.map((g) => {
            const prog = g.kind === "set" ? listProgress?.(g.listId) : null;
            const hitTarget = g.kind === "value" && g.target > 0
              ? { done: collectionValue, total: g.target }
              : null;
            return (
              <li key={g.id} className={"goal" + (g.done ? " goal--done" : "")}>
                <div className="goal-main">
                  <button
                    className="goal-tick"
                    onClick={() => toggleDone(g.id)}
                    aria-pressed={g.done}
                    title={g.done ? "Still working on it" : "Mark as done"}
                  >
                    {g.done ? "✓" : ""}
                  </button>
                  <span className="goal-text">{g.text}</span>
                  <button
                    className="goal-remove"
                    onClick={() => removeGoal(g.id)}
                    aria-label={`Remove goal: ${g.text}`}
                  >
                    ×
                  </button>
                </div>

                {/* A rule has no progress bar on purpose — there is no finish
                    line to draw, and inventing one would misrepresent it. */}
                {prog && prog.total > 0 && (
                  <Progress done={prog.done} total={prog.total} label={`${prog.done} of ${prog.total}`} />
                )}
                {hitTarget && (
                  <Progress
                    done={hitTarget.done}
                    total={hitTarget.total}
                    label={`${formatUSD(hitTarget.done)} of ${formatUSD(hitTarget.total)}`}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!adding ? (
        <div className="goal-add-row">
          {KINDS.map((k) => (
            <button key={k.id} className="btn btn-secondary" onClick={() => setAdding(k.id)}>
              + {k.label}
            </button>
          ))}
        </div>
      ) : (
        <div className="goal-form">
          <div className="stack-label">{KINDS.find((k) => k.id === adding)?.hint}</div>

          {adding === "rule" && (
            <input
              autoFocus
              className="lists-input goal-input"
              value={text}
              placeholder="Singles over slabs"
              aria-label="Your rule"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") reset(); }}
            />
          )}

          {adding === "set" && (
            <select
              autoFocus
              className="set-select goal-input"
              value={listId}
              aria-label="Which list"
              onChange={(e) => setListId(e.target.value)}
            >
              <option value="">Pick a list…</option>
              {lists.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          )}

          {adding === "value" && (
            <input
              autoFocus
              className="lists-input goal-input"
              type="number"
              min={1}
              value={target}
              placeholder="5000"
              aria-label="Target value in dollars"
              onChange={(e) => setTarget(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") reset(); }}
            />
          )}

          <button className="btn btn-primary btn-sm" onClick={submit}>Add</button>
          <button className="btn btn-secondary btn-sm" onClick={reset}>Cancel</button>
        </div>
      )}
    </>
  );
}
